import { localStorageProp, Signal } from '@tempots/dom'
import { apiRequest } from '../lib/api-client'
import type { CustomizeFormValues } from '../models/customize-form-schemas'
import { Track } from './track'
import { getAuthState } from '@/stores/auth-store'
import type { SavedDesign } from '@rh/shared/schemas/design'

export type PlanType = 'free' | 'trial' | 'standard' | 'pro' | 'team'

export interface AgencyIdentityPreferences {
  name?: string
  url?: string
  phone?: string
  email?: string
  linkedIn?: string
  addressLine1?: string
  addressLine2?: string
  addressLine3?: string
}

export interface AgencyMemberPreferences {
  name?: string
  email?: string
  phone?: string
  linkedIn?: string
}

export interface AssetMetadata {
  hash: string
  contentType: string
  uploadedAt: string
  key: string
}

export interface AgencyPreferences {
  logo?: string
  headerLogoMeta?: AssetMetadata
  footerLogoMeta?: AssetMetadata
  agency?: AgencyIdentityPreferences
  member?: AgencyMemberPreferences
  customize?: CustomizeFormValues
  /** WYSIWYG design-editor document — supersedes `customize`. */
  design?: SavedDesign
  /** Server-written fingerprint of the design onboarding seeded. */
  designSeedFingerprint?: string
}

export type AgencyRole = 'owner' | 'admin' | 'member'

export interface Agency {
  id: string
  name: string
  plan: PlanType
  planMetadata?: Record<string, unknown>
  preferences?: AgencyPreferences
  createdAt: string
  updatedAt: string
  /** The current user's membership role, present on GET /agencies items. */
  memberRole?: AgencyRole
}

export interface UserAgenciesResponse {
  agencies: Agency[]
  total: number
}

export interface CreateAgencyRequest {
  name: string
  plan?: PlanType
  planMetadata?: Record<string, unknown>
  preferences?: AgencyPreferences
}

export interface UpdateAgencyPreferencesRequest {
  logo?: string
  agency?: AgencyIdentityPreferences
  member?: AgencyMemberPreferences
  customize?: CustomizeFormValues
  design?: SavedDesign
}

let userAgencies: UserAgenciesResponse | null = null
let cachedUserId: string | null = null

function getCurrentUserId(): string | null {
  return getAuthState().user?.id ?? null
}

function clearUserAgenciesCache() {
  userAgencies = null
  cachedUserId = null
}

async function listUserAgencies({
  abortSignal,
}: {
  abortSignal?: AbortSignal
} = {}): Promise<UserAgenciesResponse> {
  const currentUserId = getCurrentUserId()

  if (userAgencies != null && cachedUserId === currentUserId) {
    return userAgencies
  }

  if (userAgencies != null && cachedUserId !== currentUserId) {
    // Prevent cross-user cache leakage across signout/signin in the same SPA session.
    clearUserAgenciesCache()
  }

  userAgencies = await apiRequest<UserAgenciesResponse>('/agencies', {
    method: 'GET',

    signal: abortSignal,
  })
  cachedUserId = currentUserId
  return userAgencies!
}

async function getAgency(agencyId: string): Promise<Agency> {
  return apiRequest<Agency>(`/agencies/${agencyId}`, {
    method: 'GET',
  })
}

async function getAgencyPreferences(
  agencyId: string
): Promise<AgencyPreferences> {
  return apiRequest<AgencyPreferences>(`/agencies/${agencyId}/preferences`, {
    method: 'GET',
  })
}

async function updateAgencyPreferences(
  agencyId: string,
  preferences: UpdateAgencyPreferencesRequest
): Promise<AgencyPreferences> {
  return apiRequest<AgencyPreferences>(`/agencies/${agencyId}/preferences`, {
    method: 'PUT',

    json: preferences,
  })
}

async function createAgency(payload: CreateAgencyRequest): Promise<Agency> {
  return apiRequest<Agency>('/agencies', {
    method: 'POST',

    json: payload,
  })
}

export interface UpdateAgencyRequest {
  name?: string
}

async function updateAgency(
  agencyId: string,
  payload: UpdateAgencyRequest
): Promise<Agency> {
  const response = await apiRequest<Agency>(`/agencies/${agencyId}`, {
    method: 'PUT',

    json: payload,
  })
  // Clear cache so next list call fetches fresh data
  clearUserAgenciesCache()
  return response
}

const preferredAgency = localStorageProp<string | null>({
  key: 'rh:preferredAgency',
  defaultValue: null,
})

async function updatePreferredAgency(agencyId: string) {
  const agencies = await listUserAgencies()
  if (agencies.total === 0) {
    clearUserAgenciesCache()
    void Track.setAgency()
    return
  }
  const resolvedAgency = agencies.agencies.find(a => a.id === agencyId)
  const nextAgency = resolvedAgency ?? agencies.agencies[0]
  preferredAgency.set(nextAgency.id)
  void Track.setAgency({ id: nextAgency.id, name: nextAgency.name })
}

function reloadUserAgencies(options: { abortSignal?: AbortSignal } = {}) {
  clearUserAgenciesCache()
  return listUserAgencies({ abortSignal: options.abortSignal })
}

export interface AgencyMember {
  id: string
  email: string
  fullName?: string
  role: AgencyRole
  joinedAt: string
  isActive: boolean
  /** True until the invitee sets a password via their invitation link. */
  invitePending?: boolean
  /**
   * When the pending invitation link expires; null when no live link
   * remains (expired or revoked). Only present while invitePending.
   */
  inviteExpiresAt?: string | null
  createdAt: string
  updatedAt: string
}

export interface AgencyMembersResponse {
  members: AgencyMember[]
  total: number
  limit: number
  offset: number
}

async function listMembers(agencyId: string): Promise<AgencyMembersResponse> {
  return apiRequest<AgencyMembersResponse>(`/agencies/${agencyId}/members`, {
    method: 'GET',
  })
}

async function addMember(
  agencyId: string,
  payload: { email: string; role: AgencyRole }
): Promise<AgencyMember> {
  return apiRequest<AgencyMember>(`/agencies/${agencyId}/members`, {
    method: 'POST',
    json: payload,
  })
}

async function updateMemberRole(
  agencyId: string,
  userId: string,
  role: AgencyRole
): Promise<AgencyMember> {
  return apiRequest<AgencyMember>(`/agencies/${agencyId}/members/${userId}`, {
    method: 'PUT',
    json: { role },
  })
}

async function resendInvite(agencyId: string, userId: string): Promise<void> {
  await apiRequest<{ message: string }>(
    `/agencies/${agencyId}/members/${userId}/resend-invite`,
    { method: 'POST' }
  )
}

async function removeMember(agencyId: string, userId: string): Promise<void> {
  await apiRequest<{ message: string }>(
    `/agencies/${agencyId}/members/${userId}`,
    { method: 'DELETE' }
  )
}

/** Leave the agency yourself. The last owner must transfer ownership first. */
async function leaveAgency(agencyId: string): Promise<void> {
  await apiRequest<{ message: string }>(`/agencies/${agencyId}/members/leave`, {
    method: 'POST',
    json: {},
  })
}

/** Customer-facing plan names; storage keeps the legacy team value. */
export const PLAN_LABELS: Record<PlanType, string> = {
  free: 'Free',
  trial: 'Trial',
  standard: 'Standard',
  pro: 'Pro',
  team: 'Agency',
}

export interface AgencyPlanInfo {
  plan: PlanType
  /** Staff-comped: team features, unlimited seats, never billed. */
  complimentary?: boolean
  /** ISO end of the 14-day trial; only set while the stored plan is trial. */
  trialEndsAt?: string | null
  /** True once a trial has lapsed without a paid plan — drives the "pick a plan" prompt. */
  trialExpired?: boolean
  /** Email of the agency owner, so members know who manages the plan. */
  ownerEmail?: string | null
  /** Distinct resumes allowed per month; null = unlimited */
  resumesPerMonth?: number | null
  /** Distinct resumes processed this month (only when limited) */
  resumesUsed?: number | null
  currentUsers: number
  maxUsers: number | null
  userLimitReached: boolean
  availableFeatures: string[]
}

async function getPlanInfo(agencyId: string): Promise<AgencyPlanInfo> {
  return apiRequest<AgencyPlanInfo>(`/agencies/${agencyId}/plan`, {
    method: 'GET',
  })
}

export interface AgencyBillingState {
  /** False when Stripe isn't configured (beta plan switch remains). */
  available: boolean
  hasStripeCustomer: boolean
  complimentary?: boolean
  billing: {
    stripeSubscriptionId?: string
    status?: string
    priceId?: string
    quantity?: number
    currentPeriodEnd?: string
    cancelAtPeriodEnd?: boolean
    cancelAt?: string
  } | null
}

async function getBillingState(agencyId: string): Promise<AgencyBillingState> {
  return apiRequest<AgencyBillingState>(`/agencies/${agencyId}/billing`, {
    method: 'GET',
  })
}

/** Start Stripe Checkout for a first subscription; returns the redirect URL. */
async function startCheckout(
  agencyId: string,
  plan: 'standard' | 'pro' | 'team'
): Promise<{ url: string }> {
  return apiRequest<{ url: string }>(`/agencies/${agencyId}/billing/checkout`, {
    method: 'POST',
    json: { plan },
  })
}

/**
 * Change an existing subscription. Paid→paid switches immediately
 * (prorated); 'free' cancels at period end; re-picking the current plan
 * clears a pending cancellation.
 */
async function changeSubscription(
  agencyId: string,
  plan: 'free' | 'standard' | 'pro' | 'team'
): Promise<{
  plan?: PlanType
  pendingCancellation?: boolean
  resumed?: boolean
}> {
  const result = await apiRequest<{
    plan?: PlanType
    pendingCancellation?: boolean
    resumed?: boolean
  }>(`/agencies/${agencyId}/billing/change`, {
    method: 'POST',
    json: { plan },
  })
  clearUserAgenciesCache()
  return result
}

/** Owner buys/releases seats on the paid Agency plan (min = filled). */
async function setSeats(
  agencyId: string,
  seats: number
): Promise<{ seats: number; filled: number }> {
  const result = await apiRequest<{ seats: number; filled: number }>(
    `/agencies/${agencyId}/billing/seats`,
    { method: 'POST', json: { seats } }
  )
  return result
}

/** Stripe Billing Portal (payment methods, invoices); returns the URL. */
async function openBillingPortal(agencyId: string): Promise<{ url: string }> {
  return apiRequest<{ url: string }>(`/agencies/${agencyId}/billing/portal`, {
    method: 'POST',
  })
}

async function getMyAgencyRole(agencyId: string): Promise<AgencyRole | null> {
  const { agencies } = await listUserAgencies()
  return agencies.find(a => a.id === agencyId)?.memberRole ?? null
}

/** Owners and admins manage the branding template; plain members do not. */
function canManageTemplate(role: AgencyRole | null): boolean {
  return role === 'owner' || role === 'admin'
}

/** Owners and admins manage the agency roster; only owners touch admins. */
function canManageMembers(role: AgencyRole | null): boolean {
  return role === 'owner' || role === 'admin'
}

export const AgencyService = {
  listUserAgencies,
  getAgency,
  getAgencyPreferences,
  updateAgencyPreferences,
  createAgency,
  updateAgency,
  preferredAgency: preferredAgency as Signal<string | null>,
  updatePreferredAgency,
  reloadUserAgencies,
  clearUserAgenciesCache,
  getMyAgencyRole,
  canManageTemplate,
  canManageMembers,
  listMembers,
  addMember,
  updateMemberRole,
  removeMember,
  leaveAgency,
  resendInvite,
  getPlanInfo,
  getBillingState,
  startCheckout,
  changeSubscription,
  openBillingPortal,
  setSeats,
}
